import CallToActionIcon from "@mui/icons-material/CallToAction";
import FilterListIcon from "@mui/icons-material/FilterList";
import InfoIcon from "@mui/icons-material/Info";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import { GlobalStyles } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { PowerBIEmbed } from "powerbi-client-react";
import React, { useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { Action, ActionBar, Breadcrumb, BreadcrumbPart, Content, ErrorTemplate, Footer, Header, SideNav, TopNav } from "zebdsh.appframe";
import ReportingClient from "./clients/ReportingClient";
import { FullscreenInfo } from "./FullscreenInfo";
import { globalTheme } from "./Theming";
import { STATE_ERROR, STATE_LOADING, STATE_SUCCESS } from "./types";
import { applyBookmark, bookmarksExists, createEmbedConfig, getLanguagePrefix } from "./utils";
const reportingClient = new ReportingClient("/api/reporting/v1.0");
const COOKIE_NAME = "zebdsh_reporting";
const CMD_SHOW = "show";
const CMD_HIDE = "hide";
const css = {
    ".reportstyles": {
        width: "100%",
        height: "calc(100vh - 9rem)",
        marginTop: "6rem",
        "& > iframe": {
            border: "none",
        },
    },
};
export const themeAppFrame = createTheme({});
const theGlobalStyles = React.createElement(GlobalStyles, { styles: (theme) => css });
export const App = (props) => {
    const [t, i18n] = useTranslation();
    const [cookies, setCookie] = useCookies([COOKIE_NAME]);
    const [pages, setPages] = useState([]);
    const [selectedPage, setSelectedPage] = useState(null);
    const [appState, setAppState] = useState(STATE_LOADING);
    const [error, setError] = useState(null);
    const [reportConfig, setReportConfig] = useState(null);
    const [bookmarks, setBookmarks] = useState(null);
    const [fullscreenInfoOpen, setFullscreenInfoOpen] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [tabsVisible, setTabsVisible] = useState(cookies[COOKIE_NAME] ? cookies[COOKIE_NAME]["isTabVisible"] : true);
    const [filterState, setFilterState] = useState({});
    const [pageTooltip, setPageTooltip] = useState(false);
    const [filterTooltip, setFilterTooltip] = useState(false);
    const [tabTooltip, setTabTooltip] = useState(false);
    const [fullscreenTooltip, setFullscreenTooltip] = useState(false);
    const reportConfigRef = useRef();
    reportConfigRef.current = reportConfig;
    useEffect(() => {
        if (!cookies[COOKIE_NAME]) {
            let content = {};
            content["isTabVisible"] = true;
            content["tooltipShown"] = true;
            setFullscreenTooltip(true);
            setTabTooltip(true);
            setFilterTooltip(true);
            setPageTooltip(true);
            setCookie(COOKIE_NAME, content, { path: "/", maxAge: 5184000, secure: true });
        }
        document.addEventListener("fullscreenchange", onFullscreenChanged, false);
        document.addEventListener("mozfullscreenchange", onFullscreenChanged, false);
        document.addEventListener("MSFullscreenChange", onFullscreenChanged, false);
        document.addEventListener("webkitfullscreenchange", onFullscreenChanged, false);
        loadEmbedToken();
        let intervalId = setInterval(() => {
            if (reportConfigRef.current) {
                let date = new Date(reportConfigRef.current.expiration);
                let current = new Date();
                let toCompare = new Date(date.getTime() - 3 * 60000);
                if (toCompare < current) {
                    loadEmbedToken();
                }
            }
        }, 10000);
        return () => {
            clearInterval(intervalId);
        };
    }, []);
    useEffect(() => {
        if (reportConfigRef.current && window.report) {
            window.report.setAccessToken(reportConfigRef.current.accessToken);
        }
    }, [reportConfig]);
    const loadEmbedToken = () => {
        let languagePrefix = getLanguagePrefix(props.appUrl);
        reportingClient.loadEmbedToken(languagePrefix, props.appName, (response) => {
            let accessToken = response.token;
            let workspaceId = response.workspaceId;
            let reportId = response.reportId;
            setReportConfig({ accessToken: accessToken, workspaceId: workspaceId, reportId: reportId, expiration: response.expiration });
            setAppState(STATE_SUCCESS);
        }, (error) => setErrorState(error));
    };
    let setErrorState = (error) => {
        console.error("An error occurred while initializing application: " + error.message);
        setError(error);
        setAppState(STATE_ERROR);
    };
    const onReportLoaded = () => {
        window.report.getPages().then((pages) => {
            setPages(pages);
        });
        window.report.bookmarksManager.getBookmarks().then((bookmarks) => setBookmarks(bookmarks));
    };
    const onFullscreenChanged = () => {
        setIsFullscreen(document.webkitIsFullScreen || document.mozFullScreen || document.msFullscreenElement);
    };
    const onPageChanged = (event) => setSelectedPage(event.detail.newPage);
    const createBreadcrumbParts = () => (React.createElement(React.Fragment, null,
        React.createElement(BreadcrumbPart, { appModel: props.appModel, entry: {
                label: props.localizedName,
            } }),
        selectedPage ? (React.createElement(BreadcrumbPart, { appModel: props.appModel, entry: {
                label: selectedPage.displayName,
                tooltip: {
                    open: pageTooltip,
                    label: t("common:tooltip page selection"),
                    onClose: () => setPageTooltip(false),
                },
                values: pages
                    .filter((page) => page.visibility == 0)
                    .map((page) => {
                    return { label: page.displayName, id: page.displayName };
                }),
                onValueSelected: (value) => {
                    let pageFromValue = pages.filter((page) => page.displayName == value)[0];
                    setSelectedPage(pageFromValue);
                    window.report.setPage(pageFromValue.name);
                },
            } })) : (React.createElement(React.Fragment, null))));
    const setFullscreen = () => {
        if (document.fullscreenEnabled || document.mozFullscreenEnabled || document.webkitFullscreenEnabled || document.msFullscreenEnabled) {
            document.body.requestFullscreen();
        }
        else {
            setFullscreenInfoOpen(true);
        }
    };
    const toggleFilter = () => {
        if (selectedPage) {
            let filterStateClone = Object.assign({}, filterState);
            filterStateClone[selectedPage.displayName] = !filterStateClone[selectedPage.displayName];
            if (filterStateClone[selectedPage.displayName]) {
                applyBookmark(selectedPage.displayName, CMD_SHOW, bookmarks);
            }
            else {
                applyBookmark(selectedPage.displayName, CMD_HIDE, bookmarks);
            }
            setFilterState(filterStateClone);
        }
    };
    const toggleTabsVisible = () => {
        if (cookies[COOKIE_NAME]) {
            let content = cookies[COOKIE_NAME];
            content["isTabVisible"] = !tabsVisible;
            setCookie(COOKIE_NAME, content, { path: "/", maxAge: 5184000, secure: true });
        }
        setTabsVisible(!tabsVisible);
    };
    const createActions = () => {
        return (React.createElement(React.Fragment, null,
            selectedPage && bookmarksExists(selectedPage.displayName, bookmarks) ? (React.createElement(Action, { appModel: props.appModel, action: {
                    label: t("common:action filter"),
                    onClick: () => toggleFilter(),
                    image: React.createElement(FilterListIcon, null),
                    toggleAction: true,
                    checked: selectedPage && filterState[selectedPage.displayName],
                    tooltip: {
                        open: filterTooltip,
                        label: t("common:tooltip filter"),
                        onClose: () => setFilterTooltip(false),
                        placement: "left",
                    },
                } })) : (React.createElement(React.Fragment, null)),
            isFullscreen ? (React.createElement(React.Fragment, null)) : (React.createElement(Action, { appModel: props.appModel, action: {
                    label: t("common:action fullscreen"),
                    onClick: setFullscreen,
                    image: React.createElement(ZoomOutMapIcon, null),
                    tooltip: {
                        open: fullscreenTooltip,
                        label: t("common:tooltip fullscreen"),
                        onClose: () => setFullscreenTooltip(false),
                        maxWidth: 200,
                        placement: "bottom-end",
                    },
                } })),
            React.createElement(Action, { appModel: props.appModel, action: {
                    label: t("common:action toggle tabs"),
                    onClick: () => toggleTabsVisible(),
                    image: React.createElement(CallToActionIcon, null),
                    toggleAction: true,
                    checked: tabsVisible,
                    tooltip: {
                        open: tabTooltip,
                        label: t("common:tooltip toggle tabs"),
                        onClose: () => setTabTooltip(false),
                        maxWidth: 100,
                        placement: "bottom-start",
                    },
                } }),
            React.createElement(Action, { appModel: props.appModel, action: {
                    label: t("common:action help"),
                    onClick: () => {
                        setTabTooltip(true);
                        setFullscreenTooltip(true);
                        setPageTooltip(true);
                        setFilterTooltip(true);
                    },
                    image: React.createElement(InfoIcon, null),
                } })));
    };
    const renderContent = () => {
        if (appState == STATE_LOADING) {
            return (React.createElement("div", { className: "zebhourglass-container" },
                React.createElement("div", { className: "zebhourglass" })));
        }
        else if (appState == STATE_ERROR) {
            return (React.createElement("div", { style: { marginTop: "6rem" } },
                React.createElement(ErrorTemplate, { statusCode: error.status, languageCode: props.userLang, resourceUrl: props.appModel.pathToStatic })));
        }
        else {
            if (!reportConfig) {
                throw Error("no report Config found");
            }
            let embedConfiguration = createEmbedConfig(reportConfig, tabsVisible, props.userLang);
            let handlers = new Map([
                ["loaded", onReportLoaded],
                ["pageChanged", onPageChanged],
            ]);
            return (React.createElement(PowerBIEmbed, { cssClassName: "reportstyles report-container", embedConfig: embedConfiguration, key: reportConfig.reportId, getEmbeddedComponent: (report) => (window.report = report), eventHandlers: handlers }));
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(ThemeProvider, { theme: themeAppFrame },
            theGlobalStyles,
            React.createElement(Header, { appModel: props.appModel },
                React.createElement(Breadcrumb, { appModel: props.appModel }, createBreadcrumbParts()),
                React.createElement(SideNav, { appModel: props.appModel }),
                React.createElement(TopNav, { appModel: props.appModel }),
                React.createElement(ActionBar, { appModel: props.appModel }, createActions())),
            React.createElement(Content, null,
                React.createElement(ThemeProvider, { theme: globalTheme },
                    renderContent(),
                    fullscreenInfoOpen ? React.createElement(FullscreenInfo, { onClose: () => setFullscreenInfoOpen(false) }) : React.createElement(React.Fragment, null))),
            !isFullscreen && React.createElement(Footer, { appModel: props.appModel }))));
};
